import { useState, useRef, useMemo, useEffect, useCallback } from 'react';
import { cr, Utils } from 'mw-style-react';

const VIDEO_REFRESH_DELAY = 1000;
const TRY_FIND_VIDEO_MULTIPLY = 200;
const FORCE_VIDEO_REFRESH_DELAY = 100;
const MAX_TIME_SINCELAST_CHANGE = 30000;
const MAX_TIME_FOR_BUFFERING = 20000;
const NETWORK_NORMAL_QUALITY_TIMER_VALUE = 5000;
const NETWORK_2G_QUALITY_TIMER_VALUE = 10000;
const NETWORK_SLOW_2G_QUALITY_TIMER_VALUE = 15000;

/**
 * Custom hook for handling video track refresh, network issues, and buffering
 * @param {Object} trackReference - LiveKit track reference
 * @param {boolean} isScreenShareActive - Whether screen share is active
 * @param {Object} videoTrackComponentRef - Reference to VideoTrack component
 * @returns {Object} Hook utilities and state
 */
const useVideoTrackRefresh = (
  trackReference,
  isScreenShareActive,
  videoTrackComponentRef
) => {
  // State management
  const [shouldRefreshVideo, setShouldRefreshVideo] = useState(false);
  const [isBuffering, setIsBuffering] = useState(true);
  const [networkQuality, setNetworkQuality] = useState('good');
  const [videoElement, setVideoElement] = useState(null);
  const [videoStalled, setVideoStalled] = useState(true);

  // Refs for tracking
  const lastVisibilityChange = useRef(Date.now());
  const lastVideoUpdate = useRef(Date.now());
  const lastFrameTime = useRef(Date.now());
  const bufferingStartTime = useRef(null);
  const videoTrackRef = useRef(null);

  /**
   * Throttled force video track refresh
   */
  const forceVideoRefresh = useMemo(
    () =>
      Utils.throttle(() => {
        const videoEl = videoTrackComponentRef?.current;
        if (!videoEl || !videoEl.paused) return;

        // Safari-friendly setup
        videoEl.muted = true;
        videoEl.autoplay = true;
        videoEl.playsInline = true;
        videoEl.webkitPlaysInline = true;
        videoEl.setAttribute('playsinline', '');

        videoEl.load();

        // Wait for ready state, then play
        const tryPlay = () => {
          if (videoEl.readyState >= 3) {
            // HAVE_FUTURE_DATA
            videoEl.play().catch(() => {});
            setVideoElement(videoEl);
          } else {
            videoEl.addEventListener(
              'canplaythrough',
              () => {
                videoEl.play().catch(() => {});
                setVideoElement(videoEl);
              },
              { once: true }
            );
          }
        };

        tryPlay();
      }, FORCE_VIDEO_REFRESH_DELAY),
    []
  );

  /**
   * Throttled method for safe refresh video with timeout
   */
  const refreshVideo = useMemo(
    () =>
      Utils.throttle(() => {
        setShouldRefreshVideo(true);
        setTimeout(() => setShouldRefreshVideo(false), VIDEO_REFRESH_DELAY);
      }, VIDEO_REFRESH_DELAY),
    []
  );

  /**
   * Create event handlers with proper closures
   */
  const createEventHandlers = useCallback(
    () => ({
      handleWaiting: () => {
        setIsBuffering(true);
        bufferingStartTime.current = Date.now();
      },

      handlePlaying: () => {
        setIsBuffering(false);
        setVideoStalled(false);
        const timeNow = Date.now();
        bufferingStartTime.current = null;
        lastVideoUpdate.current = timeNow;
        lastFrameTime.current = timeNow;
      },

      handleStalled: () => {
        setVideoStalled(true);
      },

      handleError: () => {
        setTimeout(() => forceVideoRefresh(), VIDEO_REFRESH_DELAY);
      },

      handleTimeUpdate: () => {
        const timeNow = Date.now();
        lastVideoUpdate.current = timeNow;
        lastFrameTime.current = timeNow;
        // Always reset stalled state on time update - no closure issue
        setVideoStalled(false);
      },
    }),
    [forceVideoRefresh]
  );

  /**
   * Setup video element event listeners
   */
  const setupVideoEventListeners = useCallback(
    (videoEl) => {
      if (!videoEl) return false;

      // Clean up previous listeners if they exist
      if (videoEl._cleanup) {
        videoEl._cleanup();
      }

      const handlers = createEventHandlers();
      const videoEventHandlers = new Map([
        ['waiting', handlers.handleWaiting],
        ['playing', handlers.handlePlaying],
        ['stalled', handlers.handleStalled],
        ['error', handlers.handleError],
        ['timeupdate', handlers.handleTimeUpdate],
      ]);

      // Add event listeners
      videoEventHandlers.forEach((handler, event) => {
        videoEl.addEventListener(event, handler);
      });

      // Store cleanup function
      videoEl._cleanup = () => {
        videoEventHandlers.forEach((handler, event) => {
          videoEl.removeEventListener(event, handler);
        });
      };

      return true;
    },
    [createEventHandlers]
  );

  /**
   * Clean up video element and its listeners
   */
  const cleanupVideoElement = useCallback(() => {
    if (videoElement && videoElement._cleanup) {
      videoElement._cleanup();
    }
    setVideoElement(null);
    videoTrackRef.current = null;
  }, [videoElement]);

  // Monitor network connection quality
  useEffect(() => {
    const updateNetworkQuality = () => {
      const connection =
        navigator.connection ||
        navigator.mozConnection ||
        navigator.webkitConnection;
      if (connection) {
        const effectiveType = connection.effectiveType;
        setNetworkQuality(effectiveType);

        // Auto-refresh on network quality improvement
        if (['4g', '3g'].includes(effectiveType) && isScreenShareActive) {
          refreshVideo();
        }
      }
    };

    if (navigator.connection) {
      navigator.connection.addEventListener('change', updateNetworkQuality);
      updateNetworkQuality();
    }

    return () => {
      if (navigator.connection) {
        navigator.connection.removeEventListener(
          'change',
          updateNetworkQuality
        );
      }
    };
  }, [refreshVideo, isScreenShareActive]);

  // Handle page visibility changes
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        const timeSinceLastChange = Date.now() - lastVisibilityChange.current;
        if (timeSinceLastChange > MAX_TIME_SINCELAST_CHANGE) {
          refreshVideo();
        }
      }
      lastVisibilityChange.current = Date.now();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () =>
      document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [refreshVideo]);

  // Combined effect for video element detection and track reference changes
  useEffect(() => {
    if (isScreenShareActive && videoTrackComponentRef?.current) {
      const tryFindVideo = (attempts = 0) => {
        const videoEl = videoTrackComponentRef?.current;

        if (videoEl !== videoElement) {
          cleanupVideoElement();
          setVideoElement(videoEl);
          videoTrackRef.current = videoEl;
          setupVideoEventListeners(videoEl);
        } else if (attempts < 5) {
          const delay =
            networkQuality === 'slow-2g'
              ? VIDEO_REFRESH_DELAY
              : TRY_FIND_VIDEO_MULTIPLY * (attempts + 1);
          setTimeout(() => tryFindVideo(attempts + 1), delay);
        }
      };

      tryFindVideo();
    } else {
      cleanupVideoElement();
    }
  }, [
    isScreenShareActive,
    trackReference?.participant?.identity,
    trackReference?.publication?.trackSid,
    networkQuality,
    setupVideoEventListeners,
    cleanupVideoElement,
    videoElement,
  ]);

  // Handle video refresh when needed
  useEffect(() => {
    if (shouldRefreshVideo) {
      forceVideoRefresh();
    }
  }, [shouldRefreshVideo, forceVideoRefresh]);

  // Monitor video element health and network conditions
  useEffect(() => {
    if (videoElement && isScreenShareActive) {
      const checkVideoHealth = () => {
        const now = Date.now();
        const timeSinceLastFrame = now - lastFrameTime.current;
        const timeSinceLastUpdate = now - lastVideoUpdate.current;

        // Check for various stalling conditions
        const isLikelyStalled =
          timeSinceLastFrame > NETWORK_2G_QUALITY_TIMER_VALUE ||
          timeSinceLastUpdate > NETWORK_SLOW_2G_QUALITY_TIMER_VALUE ||
          videoElement.readyState < 2 ||
          (videoElement.paused && !videoElement.ended);

        // Check buffering duration
        const bufferingTooLong =
          bufferingStartTime.current &&
          now - bufferingStartTime.current > MAX_TIME_FOR_BUFFERING;

        if (isLikelyStalled || bufferingTooLong || videoStalled) {
          // Different recovery strategies based on network quality
          if (networkQuality === 'slow-2g' || networkQuality === '2g') {
            setTimeout(
              () => forceVideoRefresh(),
              NETWORK_NORMAL_QUALITY_TIMER_VALUE
            );
          } else {
            forceVideoRefresh();
          }
        }
      };

      // Check more frequently on slower networks
      const checkInterval = cr(
        [networkQuality === 'slow-2g', NETWORK_SLOW_2G_QUALITY_TIMER_VALUE],
        [networkQuality === '2g', NETWORK_2G_QUALITY_TIMER_VALUE],
        [true, NETWORK_NORMAL_QUALITY_TIMER_VALUE]
      );

      const healthCheckInterval = setInterval(checkVideoHealth, checkInterval);
      return () => clearInterval(healthCheckInterval);
    }
  }, [
    videoElement,
    isScreenShareActive,
    networkQuality,
    videoStalled,
    forceVideoRefresh,
  ]);

  // Auto-refresh on network quality improvement
  useEffect(() => {
    if (networkQuality === '4g' && (videoStalled || isBuffering)) {
      refreshVideo();
    }
  }, [networkQuality, videoStalled, isBuffering, refreshVideo]);

  return {
    // State
    videoElement,
    isBuffering,
    videoStalled,
    networkQuality,
    shouldRefreshVideo,

    // Methods
    forceVideoRefresh,
    refreshVideo,
    setupVideoEventListeners,
    cleanupVideoElement,

    // Refs
    videoTrackRef,
    lastFrameTime,
    bufferingStartTime,
  };
};

export default useVideoTrackRefresh;
